import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { useToast } from '@/hooks/use-toast';
import { useGetTemplateBySlug } from '@/modules/templates/hooks/use-templates';
import {
  deleteTemplateScreen,
  getFieldsByScreenId,
  getScreensByTemplateId,
  toScreen,
  toField,
  updateTemplateScreen,
} from '@/modules/templates/services/templates.service';
import type {
  TemplateField,
  TemplateScreen,
  UdsTemplateField,
  UdsTemplateScreen,
} from '@/modules/templates/types/templates.types';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  ChevronRight,
  FileText,
  GripVertical,
  Loader2,
  PanelRightClose,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AIConfigurator } from '../builder/ai-configurator';
import { DeleteDialog } from '../builder/delete-dialog';
import { FormBuilderDnd } from '../builder/form-builder-dnd';
import { ScreenEditor } from '../builder/screen-editor';

interface ScreenWithFields extends TemplateScreen {
  fields: TemplateField[];
}

interface SortableScreenItemProps {
  screen: ScreenWithFields;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  fieldCountLabel: (count: number) => string;
}

function SortableScreenItem({
  screen,
  index,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  fieldCountLabel,
}: SortableScreenItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: screen.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${
        isSelected ? 'bg-selise-blue/10 border border-selise-blue/20' : 'hover:bg-border/50'
      } ${isDragging ? 'z-50' : ''}`}
      onClick={onSelect}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="h-4 w-4 text-globe-grey opacity-0 group-hover:opacity-100" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-globe-grey w-6">{index + 1}.</span>
          <span className="font-medium text-sm text-[hsl(var(--fg))] truncate">{screen.title}</span>
        </div>
        <p className="text-xs text-globe-grey ml-6">{fieldCountLabel(screen.fields.length)}</p>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <ChevronRight
        className={`h-4 w-4 transition-colors ${
          isSelected ? 'text-selise-blue' : 'text-globe-grey'
        }`}
      />
    </div>
  );
}

export default function AdminTemplateClassicBuilderPage() {
  const params = useParams();
  const navigate = useNavigate();
  const templateId = params.id as string;
  const { t } = useTranslation();
  const { toast } = useToast();

  const { data: templateData, isLoading: templateLoading } = useGetTemplateBySlug(templateId);

  const [screens, setScreens] = useState<ScreenWithFields[]>([]);
  const [selectedScreenId, setSelectedScreenId] = useState<string | null>(null);
  const [screenEditorOpen, setScreenEditorOpen] = useState(false);
  const [editingScreen, setEditingScreen] = useState<ScreenWithFields | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingScreen, setDeletingScreen] = useState<ScreenWithFields | null>(null);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);

  const template = templateData ?? null;

  const fetchScreens = useCallback(async () => {
    if (!templateData?.id) return;
    try {
      const screensResult = await getScreensByTemplateId(templateData.id);
      const rawScreens: UdsTemplateScreen[] = screensResult.items || [];

      if (rawScreens.length === 0) {
        setScreens([]);
        return;
      }

      const fieldResults = await Promise.all(rawScreens.map((s) => getFieldsByScreenId(s.ItemId)));

      const mergedScreens: ScreenWithFields[] = rawScreens
        .map((rawScreen, idx) => {
          const rawFields: UdsTemplateField[] = fieldResults[idx].items || [];
          return {
            ...toScreen(rawScreen),
            fields: rawFields.map(toField).sort((a, b) => a.order - b.order),
          };
        })
        .sort((a, b) => a.order - b.order);

      setScreens(mergedScreens);

      if (mergedScreens.length > 0) {
        setSelectedScreenId((prev) => prev ?? mergedScreens[0].id);
      }
    } catch (error) {
      console.error('Error fetching screens:', error);
      toast({
        variant: 'destructive',
        title: t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_ERRORS_LOADSCREENS'),
      });
    }
  }, [templateData?.id, t, toast]);

  useEffect(() => {
    if (templateData) {
      const screensWithFields: ScreenWithFields[] = (templateData.screens || []).map((s) => ({
        ...s,
        fields: s.fields || [],
      }));
      setScreens(screensWithFields);
      if (screensWithFields.length > 0) {
        setSelectedScreenId((prev) => prev ?? screensWithFields[0].id);
      }
    }
  }, [templateData]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddScreen = () => {
    setEditingScreen(null);
    setScreenEditorOpen(true);
  };

  const handleEditScreen = (screen: ScreenWithFields) => {
    setEditingScreen(screen);
    setScreenEditorOpen(true);
  };

  const handleDeleteScreen = (screen: ScreenWithFields) => {
    setDeletingScreen(screen);
    setDeleteDialogOpen(true);
  };

  const handleScreenSaved = async () => {
    await fetchScreens();
    setScreenEditorOpen(false);
    setEditingScreen(null);
  };

  const handleScreenDeleted = async () => {
    if (deletingScreen) {
      await deleteTemplateScreen(deletingScreen.id, true);
      await fetchScreens();
      setDeleteDialogOpen(false);

      if (deletingScreen.id === selectedScreenId) {
        const remainingScreens = screens.filter((s) => s.id !== deletingScreen.id);
        setSelectedScreenId(remainingScreens[0]?.id || null);
      }
      setDeletingScreen(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = screens.findIndex((screen) => screen.id === active.id);
    const newIndex = screens.findIndex((screen) => screen.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newScreens = arrayMove(screens, oldIndex, newIndex);
    setScreens(newScreens);

    try {
      await Promise.all(
        newScreens.map((screen, index) => updateTemplateScreen(screen.id, { Order: index + 1 }))
      );
      await fetchScreens();
      toast({
        variant: 'success',
        title: t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_TOAST_REORDERSUCCESS'),
      });
    } catch (error) {
      setScreens(screens);
      toast({
        variant: 'destructive',
        title: t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_ERRORS_REORDERSCREENS'),
      });
      console.error('Error reordering screens:', error);
    }
  };

  const selectedScreen = screens.find((s) => s.id === selectedScreenId) ?? null;

  if (templateLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[hsl(var(--bg))]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-selise-blue" />
          <p className="text-sm text-globe-grey">{t('ORGTEMPLATES_BUILDER_LOADING')}</p>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="h-screen flex items-center justify-center bg-[hsl(var(--bg))]">
        <div className="text-center max-w-md p-8">
          <h2 className="text-xl font-semibold text-[hsl(var(--fg))] mb-2">
            {t('ORGTEMPLATES_BUILDER_ERRORS_NOTFOUND')}
          </h2>
          <p className="text-sm text-globe-grey mb-6">
            {t('ORGTEMPLATES_BUILDER_ERRORS_UNABLETOLOAD')}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-selise-blue text-white font-medium hover:bg-oxford-blue transition-colors"
          >
            {t('ORGTEMPLATES_BUILDER_GOBACK')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto max-w-[95%] xl:max-w-400 py-6 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/admin/templates/${templateId}/edit`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-[hsl(var(--fg))]">
              {t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_CLASSICTITLE')}
            </h1>
            <p className="text-sm text-globe-grey">{template.title}</p>
          </div>
          <Badge variant="secondary" className="text-xs">
            {t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_SCREENSCOUNT', {
              count: screens.length,
            })}
          </Badge>
        </div>
        <Button
          variant={aiPanelOpen ? 'default' : 'outline'}
          size="sm"
          onClick={() => setAiPanelOpen(!aiPanelOpen)}
          className="gap-2"
        >
          {aiPanelOpen ? (
            <>
              <PanelRightClose className="h-4 w-4" />
              {t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_AI_CLOSE')}
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              {t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_AI_OPEN')}
            </>
          )}
        </Button>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Screens Panel */}
        <div className="w-full lg:w-64 xl:w-72 shrink-0 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[hsl(var(--fg))] uppercase tracking-wider">
              {t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_SCREENSTITLE')}
            </h3>
            <Button size="sm" onClick={handleAddScreen}>
              <Plus className="h-4 w-4 mr-1" />
              {t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_SCREENSADD')}
            </Button>
          </div>

          <Card className="border-border">
            <CardContent className="p-2">
              {screens.length === 0 ? (
                <div className="text-center py-6 text-globe-grey">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_EMPTY_TITLE')}</p>
                  <p className="text-sm mt-1">
                    {t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_EMPTY_DESCRIPTION')}
                  </p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={screens.map((s) => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-1">
                      {screens.map((screen, index) => (
                        <SortableScreenItem
                          key={screen.id}
                          screen={screen}
                          index={index}
                          isSelected={selectedScreenId === screen.id}
                          onSelect={() => setSelectedScreenId(screen.id)}
                          onEdit={(e) => {
                            e.stopPropagation();
                            handleEditScreen(screen);
                          }}
                          onDelete={(e) => {
                            e.stopPropagation();
                            handleDeleteScreen(screen);
                          }}
                          fieldCountLabel={(count) =>
                            t('ORGTEMPLATES_EDIT_FORMBUILDER_SCREENFIELDS', {
                              count,
                            })
                          }
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content Area */}
        <div className={`flex-1 min-w-0 flex flex-col lg:flex-row gap-4`}>
          <div className={`${aiPanelOpen ? 'flex-1 min-w-0' : 'flex-1'}`}>
            <FormBuilderDnd
              templateId={templateId}
              templateSlug={template?.slug ?? templateId}
              templateOrganizationId={template?.organizationId ?? null}
              screens={screens}
              selectedScreenId={selectedScreenId}
              onSelectScreen={setSelectedScreenId}
              onScreensReordered={setScreens}
              onFieldsUpdated={fetchScreens}
              aiPanelOpen={aiPanelOpen}
            />
          </div>

          {aiPanelOpen && template && (
            <div className="w-full lg:w-96 xl:w-105 shrink-0 lg:h-[calc(100vh-12rem)] lg:sticky lg:top-4">
              <AIConfigurator
                templateId={templateId}
                templateTitle={template.title}
                templateDescription={template.description ?? ''}
                screens={screens}
                selectedScreen={selectedScreen}
                onScreenCreated={async () => {
                  await fetchScreens();
                }}
                onFieldsUpdated={async () => {
                  await fetchScreens();
                }}
                onClose={() => setAiPanelOpen(false)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Screen Editor Dialog */}
      <ScreenEditor
        open={screenEditorOpen}
        onOpenChange={setScreenEditorOpen}
        templateId={templateId}
        templateSlug={template?.slug ?? templateId}
        templateOrganizationId={template?.organizationId ?? null}
        screen={editingScreen}
        allScreens={screens}
        onSaved={handleScreenSaved}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_DELETEDIALOG_TITLE')}
        description={t('ADMIN_TEMPLATES_EDIT_FORMBUILDER_DELETEDIALOG_DESCRIPTION', {
          title: deletingScreen?.title ?? '',
          fieldsCount: deletingScreen?.fields.length || 0,
        })}
        onConfirm={handleScreenDeleted}
      />
    </div>
  );
}
